import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getPageSummaries, getPageMetrics, getPagesThin, bulkSetPageStatus, getPageRevisionIds, getDomainAmazonOffers } from './api';
import { offersForPage, formatCtr } from './offerMetrics';
import { openSitePreview } from './previewLinks';
import ThinBadge from './seo/ThinBadge';
import { pageVerdict, countThin, filterThin } from './seo/seoThin';
import PagePreviewModal, { previewEnabled } from './PagePreviewModal';
import TemplateRoutes from './TemplateRoutes';
import { useDomain } from './DomainContext';
import { useToast } from './Toast';
import Spinner from './ui/Spinner';
import ConfirmDialog from './ui/ConfirmDialog';
import Badge from './ui/Badge';
import EmptyState from './ui/EmptyState';
import PageHeader from './ui/PageHeader';
import Alert from './ui/Alert';
import Sparkline, { SERIES_COLORS } from './ui/Sparkline';
import { derivePageState, goLiveAt, filterPages, countByState, STATE_LABELS } from './pageStatus';
import { mstDayWindow, dailyToSeries } from './domainMetrics';
import {
  indexMetricsBySlug,
  metricsForPage,
  isEstimated,
  formatMoney,
  formatRatio,
  formatCount,
  formatDuration,
  READ_MIN_SAMPLE,
} from './pageMetrics';
import { pageAttentionBadges, paginate } from './domainDashboard';
import { getAmazonEarnings } from './apiAnalytics';
import { getSiteRankings } from './apiRankings';
import { indexSearchByPage, searchForPage, formatPosition, positionMove } from './seo/pageSearch';
import { fmtUsd } from './donext/doNext';
import { FALLBACK_TAG, formatRange, latestAmazonForDomain } from './earnings';

const PAGE_SIZE = 50;

/* Same period and size as the Rankings tab, so both read one cached summary. */
const SEARCH_DAYS = 28;
const SEARCH_ROWS = 100;

/* The sparkline is the shared one from the Domains index — same two series,
   same per-series normalisation, same colours. */

const Dash = () => <span className="muted">—</span>;

/* What each bulk action does, for the confirm dialog and the toast. */
const BULK_ACTIONS = {
  published: { label: 'Publish', verb: 'published', danger: false,
    note: 'Pages with no publish date get one now; scheduled pages keep theirs.' },
  draft: { label: 'Set draft', verb: 'set to draft', danger: false,
    note: 'They will disappear from the live site until published again.' },
  archived: { label: 'Archive', verb: 'archived', danger: true,
    note: 'Archived pages are hidden from the site but keep their content and URL.' },
};

const rowId = (page) => page.slug || page._id || page.id;

/* Rows the site renders from its templates, not DAM pages: 'template' routes
   (site_routes.py) and 'stub' docs holding only ad stats. They can't be edited
   or bulk-changed here. */
const notDamPage = (page) => page.source === 'template' || page.source === 'stub';

const DomainPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { domainId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { selectedDomain, domains } = useDomain();
  const domain = (selectedDomain && selectedDomain.id === domainId)
    ? selectedDomain
    : (domains || []).find(d => d.id === domainId);
  const hostname = (domain && (domain.hostname || domain.name)) || domainId.replace(/_/g, '.');
  const canPreview = previewEnabled();

  /* stateFilter: 'all' | 'draft' | 'scheduled' | 'published' | 'archived' */
  const [stateFilter, setStateFilter] = useState('all');
  const [query, setQuery] = useState('');

  /* sortKey: 'title' | 'state' | 'updated' | 'hits' | 'impressions' | 'adsPerHit' | 'ecpm' | 'pageRpm' | 'earnings'
              | 'amzImpr' | 'amzClicks' | 'google' (the $ a month its queries would add at the top 3)
     Most profitable first by default: the per-1k rates hide which pages
     actually bring the money in. */
  const [sortKey, setSortKey] = useState('earnings');
  const [sortDir, setSortDir] = useState('desc');
  const [previewSlug, setPreviewSlug] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [amazon, setAmazon] = useState(null);
  /* Amazon reports per tracking ID, never per page: shown at site level only. */
  const amazonTag = domain && String(domain.amazon_tag || '').trim();
  const amazonLatest = amazonTag ? latestAmazonForDomain(amazon, domainId) : null;
  /* Our own Amazon offer impressions/clicks per page (Snowplow), unlike the
     Associates earnings above, which Amazon only reports per site. */
  const [offers, setOffers] = useState(null);
  const showOffers = !!amazonTag || !!(offers && offers.totals && offers.totals.impressions > 0);
  /* Google search per page (the Rankings summary); null until it answers */
  const [search, setSearch] = useState(null);
  /* {pages: {slug: verdict}, ...} from /pages/thin; null until it answers */
  const [thinData, setThinData] = useState(null);
  /* ?thin=1 (the home page's Do next list links here) opens on thin pages */
  const [thinOnly, setThinOnly] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('thin') === '1'; } catch { return false; }
  });
  /* slugs with a pending revision (page_drafts/<slug>); empty until it answers */
  const [revisionIds, setRevisionIds] = useState(() => new Set());

  /* paging + selection */
  const [pageNum, setPageNum] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [pendingAction, setPendingAction] = useState(null); // 'published' | 'draft' | 'archived'
  const [bulkBusy, setBulkBusy] = useState(false);
  const selectAllRef = useRef(null);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    setError('');
    /* The list comes from Firestore and the numbers from BigQuery, which is
       much the slower of the two. They are started together but awaited apart,
       so the table renders as soon as the pages land and the metric columns
       fill in when BigQuery answers. getPageMetrics swallows its own errors,
       so a BQ failure blanks those columns rather than the table. */
    const metricsPromise = getPageMetrics(domainId);
    setMetrics(null);
    metricsPromise.then((m) => setMetrics(m || null)).catch(() => setMetrics(null));
    /* Thin verdicts likewise fill in after the table: they read every page's
       body server-side. A failure just leaves the badges off. */
    getAmazonEarnings().then((a) => setAmazon(a)).catch(() => setAmazon(null));
    setOffers(null);
    getDomainAmazonOffers(domainId).then((o) => setOffers(o || null)).catch(() => setOffers(null));
    setSearch(null);
    getSiteRankings(domainId, SEARCH_DAYS, SEARCH_ROWS).then((r) => setSearch(r || null)).catch(() => setSearch(null));
    setThinData(null);
    getPagesThin(domainId).then((t) => setThinData(t || null)).catch(() => setThinData(null));
    getPageRevisionIds(domainId).then((ids) => setRevisionIds(new Set(ids || []))).catch(() => setRevisionIds(new Set()));
    try {
      const response = await getPageSummaries(domainId, { templates: true });
      const list = response || [];
      setPages(list);
      /* keep only selections that still exist after a refetch */
      const ids = new Set(list.map(rowId));
      setSelected(prev => new Set([...prev].filter(id => ids.has(id))));
    } catch (err) {
      console.error('Error fetching pages:', err);
      setError('Failed to fetch pages. Please try again.');
      setPages([]);
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  /* a new filter, search or sort starts back at page 1 */
  useEffect(() => {
    setPageNum(1);
  }, [stateFilter, query, sortKey, sortDir, showAll, thinOnly]);

  /* The page on the site's real host, in preview: drafts and pending
     revisions show there for this browser only. */
  const handleSitePreview = (page) => {
    openSitePreview(domainId, page.slug || '').catch((err) => {
      const detail = err?.response?.data?.detail;
      addToast(typeof detail === 'string' ? detail : 'Could not open the preview', 'error');
    });
  };

  const handleEditPage = (page) => {
    if (notDamPage(page)) return; // rendered by the site's template: nothing to edit here
    navigate(`/domain/${encodeURIComponent(domainId)}/pages/${encodeURIComponent(rowId(page))}/edit`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  /* ── sorting ───────────────────────────────── */
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'title' ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const metricsIndex = useMemo(
    () => indexMetricsBySlug(metrics && metrics.pages), [metrics]);
  const offersIndex = useMemo(
    () => indexMetricsBySlug(offers && offers.pages), [offers]);
  const searchIndex = useMemo(() => indexSearchByPage(search), [search]);
  /* probably-human views per page: present once the API labels views */
  const humanKnown = !!(metrics && metrics.totals && metrics.totals.human_hits != null);
  const hasSearch = !!(search && !search.error && search.totals && search.totals.impressions > 0);

  /* The server states the exact MST days its buckets cover, so the sparkline
     x-axis comes from the response rather than being re-derived here. */
  const dayKeys = useMemo(
    () => (metrics && metrics.day_keys) || mstDayWindow(30), [metrics]);

  const stateCounts = useMemo(() => countByState(pages), [pages]);

  const sortedPages = useMemo(() => {
    const thinMap = thinData && thinData.pages;
    const copy = [...filterThin(filterPages(pages, { state: stateFilter, query }), thinMap, thinOnly)];
    /* Ads/Hit and Page RPM are null for a page with no views. Sorting has to
       put those last in both directions rather than treating null as 0. */
    const nullsLast = (va, vb) => {
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return sortDir === 'asc' ? va - vb : vb - va;
    };
    copy.sort((a, b) => {
      let va, vb;
      const ma = metricsForPage(metricsIndex, a);
      const mb = metricsForPage(metricsIndex, b);
      switch (sortKey) {
        case 'title':
          va = (a.title || '').toLowerCase();
          vb = (b.title || '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'state':
          va = derivePageState(a);
          vb = derivePageState(b);
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'updated':
          va = a.updated_at || '';
          vb = b.updated_at || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'hits':
          return sortDir === 'asc' ? ma.hits - mb.hits : mb.hits - ma.hits;
        case 'human':
          return nullsLast(ma.human_hits, mb.human_hits);
        case 'readRate':
          return nullsLast(ma.read_rate, mb.read_rate);
        case 'readTime':
          return nullsLast(ma.read_median_s, mb.read_median_s);
        case 'impressions':
          return sortDir === 'asc'
            ? ma.impressions - mb.impressions
            : mb.impressions - ma.impressions;
        case 'adsPerHit':
          return nullsLast(ma.ads_per_hit, mb.ads_per_hit);
        case 'ecpm':
          return nullsLast(ma.ecpm, mb.ecpm);
        case 'pageRpm':
          return nullsLast(ma.page_rpm, mb.page_rpm);
        case 'earnings':
          return nullsLast(ma.earnings, mb.earnings);
        case 'amzImpr':
          va = offersForPage(offersIndex, a).impressions;
          vb = offersForPage(offersIndex, b).impressions;
          return sortDir === 'asc' ? va - vb : vb - va;
        case 'amzClicks':
          va = offersForPage(offersIndex, a).clicks;
          vb = offersForPage(offersIndex, b).clicks;
          return sortDir === 'asc' ? va - vb : vb - va;
        case 'google': {
          const ga = searchForPage(searchIndex, a);
          const gb = searchForPage(searchIndex, b);
          // by the money at stake, then by what Google already sends
          va = ga.upsideUsd + ga.clicks * 1e-6;
          vb = gb.upsideUsd + gb.clicks * 1e-6;
          return sortDir === 'asc' ? va - vb : vb - va;
        }
        default:
          return 0;
      }
    });
    return copy;
  }, [pages, sortKey, sortDir, metricsIndex, offersIndex, searchIndex, stateFilter, query, thinData, thinOnly]);

  const thinCounts = useMemo(() => countThin(thinData && thinData.pages, pages), [thinData, pages]);

  const paged = useMemo(
    () => paginate(sortedPages, pageNum, showAll ? 0 : PAGE_SIZE),
    [sortedPages, pageNum, showAll]);

  /* ── selection ─────────────────────────────── */
  const visibleIds = useMemo(() => paged.rows.filter((p) => !notDamPage(p)).map(rowId), [paged.rows]);
  const visibleSelected = visibleIds.filter(id => selected.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && visibleSelected === visibleIds.length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = visibleSelected > 0 && !allVisibleSelected;
    }
  }, [visibleSelected, allVisibleSelected]);

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const runBulk = async () => {
    const status = pendingAction;
    const ids = [...selected];
    setPendingAction(null);
    if (!status || ids.length === 0) return;
    setBulkBusy(true);
    try {
      const result = await bulkSetPageStatus(domainId, ids, status);
      const updated = result?.updated ?? 0;
      const failed = result?.failed || [];
      const verb = BULK_ACTIONS[status].verb;
      if (failed.length === 0) {
        addToast(`${updated} page${updated === 1 ? '' : 's'} ${verb}`, 'success');
      } else {
        addToast(`${updated} ${verb}, ${failed.length} failed: ${failed.slice(0, 3).join(', ')}${failed.length > 3 ? '…' : ''}`, 'warning', 8000);
      }
      clearSelection();
      await fetchPages();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Bulk update failed', 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const pendingMeta = pendingAction ? BULK_ACTIONS[pendingAction] : null;

  return (
    <div className="page">
      <PageHeader
        title="Pages"
        subtitle={hostname}
        actions={(
          <>
            <Link className="btn btn-secondary" to={`/domain/${domainId}`}>← Overview</Link>
            <button type="button" className="btn btn-primary" onClick={() => navigate(`/domain/${domainId}/pages/new/edit`)}>
              + New page
            </button>
          </>
        )}
      />

      {/* State chips + search. Counts come from the full list, not the current
          filter, so a chip still says how many pages it would show. */}
      <div className="toolbar">
        <div className="chip-group" role="group" aria-label="Filter by state">
          {['all', 'draft', 'scheduled', 'published', 'archived'].map((s) => (
            <button
              key={s}
              type="button"
              className={`chip${stateFilter === s ? ' active' : ''}`}
              aria-pressed={stateFilter === s}
              onClick={() => setStateFilter(s)}
            >
              {s === 'all' ? 'All' : STATE_LABELS[s]} <span className="count">{stateCounts[s]}</span>
            </button>
          ))}
        </div>
        {thinData && (
          <button
            type="button"
            className={`chip${thinOnly ? ' active' : ''}`}
            aria-pressed={thinOnly}
            onClick={() => setThinOnly((v) => !v)}
            title={'Thin pages are marked noindex and left out of the sitemap until improved. '
                 + 'Rules: Settings → Site settings → SEO: thin pages.'
                 + (thinData.sections_included === false ? ' (Section text could not be read, so word counts use the introduction only.)' : '')}
          >
            Thin only <span className="count">{thinCounts.thin.toLocaleString()} thin of {thinCounts.total.toLocaleString()}</span>
          </button>
        )}
        <input
          type="search"
          className="input grow"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title or slug…"
          aria-label="Search pages by title or slug"
          style={{ marginLeft: 'auto', maxWidth: '320px' }}
        />
      </div>

      {metrics && metrics.totals && metrics.totals.pages > 0 && (
        <div className="card" style={{ marginBottom: '16px', padding: '12px 16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'baseline', fontSize: '13px' }}>
            {[
              ['Hits (30d)', formatCount(metrics.totals.hits)],
              ...(humanKnown ? [['Human views', `${formatCount(metrics.totals.human_hits)}${metrics.totals.hits
                ? ` (${Math.round((metrics.totals.human_hits / metrics.totals.hits) * 100)}%)` : ''}`]] : []),
              ['Impressions', formatCount(metrics.totals.impressions)],
              ['Ads/Hit', formatRatio(metrics.totals.ads_per_hit)],
              ['eCPM', formatMoney(metrics.totals.ecpm)],
              ['Page RPM', formatMoney(metrics.totals.page_rpm)],
              ['AdSense earnings (30d)', formatMoney(metrics.earnings_30d)],
              ...(hasSearch ? [
                [`Google clicks (${SEARCH_DAYS}d)`, formatCount(search.totals.clicks)],
                ['Upside at top 3', search.untapped && search.untapped.top3_usd > 0
                  ? `+${fmtUsd(search.untapped.top3_usd)}/mo` : '—'],
              ] : []),
            ].map(([label, value]) => (
              <div key={label}>
                <div className="kpi-label">{label}</div>
                <div style={{ fontWeight: 600, fontSize: '15px', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
              </div>
            ))}
            {metrics.read_time && (
              <div title={'Share of page views where the reader was active (scrolling, moving, typing) for '
                          + 'at least 30 seconds, from Snowplow page pings. Views under 30s send no pings.'}>
                <div className="kpi-label">Read rate (30s+)</div>
                <div style={{ fontWeight: 600, fontSize: '15px', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCtr(metrics.read_time.read_rate)}
                  <span className="muted" style={{ fontWeight: 400, fontSize: '12px' }}>
                    {' '}{formatCount(metrics.read_time.readers)} readers
                  </span>
                </div>
              </div>
            )}
            {metrics.read_time && (
              <div title={'Of the readers (active 30s+), half stayed at least this long. Active time only: '
                          + 'a tab left in the background stops counting. Measured in 10-second steps.'}>
                <div className="kpi-label">Median read time</div>
                <div style={{ fontWeight: 600, fontSize: '15px', fontVariantNumeric: 'tabular-nums' }}>
                  {formatDuration(metrics.read_time.read_median_s)}
                </div>
              </div>
            )}
            <div title={amazonTag
              ? `Amazon reports earnings per tracking ID (${amazonTag}), never per page, so this is the whole site.`
              : `This site has no Amazon tracking ID; its links use ${FALLBACK_TAG}, which cannot be attributed to a site.`}>
              <div className="kpi-label">
                Amazon{amazonLatest ? ` (${formatRange(amazonLatest.period_start, amazonLatest.period_end)}, site only)` : ' (site only)'}
              </div>
              <div style={{ fontWeight: 600, fontSize: '15px', fontVariantNumeric: 'tabular-nums' }}>
                {!amazonTag ? <span className="muted" style={{ fontWeight: 400, fontSize: '13px' }}>no tracking ID</span>
                  : amazonLatest ? formatMoney(amazonLatest.earnings)
                  : <span className="muted" style={{ fontWeight: 400, fontSize: '13px' }}>no report yet</span>}
              </div>
            </div>
            {showOffers && offers && offers.totals && (
              <div title="Our own count of Amazon offers seen (at least half in view) and clicked on this site's pages, from Snowplow. Amazon's click and order numbers come from the Associates report and will differ.">
                <div className="kpi-label">
                  <Link to={`/domain/${domainId}/offers`}>Amazon offers (30d)</Link>
                </div>
                <div style={{ fontWeight: 600, fontSize: '15px', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCount(offers.totals.impressions || 0)} impr · {formatCount(offers.totals.clicks || 0)} clicks
                  <span className="muted" style={{ fontWeight: 400, fontSize: '12px' }}> {formatCtr(offers.totals.ctr)} CTR</span>
                </div>
              </div>
            )}
            <div className="muted" style={{ marginLeft: 'auto', fontSize: '11px', maxWidth: '340px' }}>
              Live from BigQuery — {metrics.totals.pages.toLocaleString()} pages over the
              last {metrics.period_days} complete MST days (today excluded).
              {metrics.coverage && (
                metrics.coverage.measured_pages > 0
                  ? <> AdSense reported {metrics.coverage.measured_pages.toLocaleString()} of
                      {' '}{metrics.totals.pages.toLocaleString()} pages individually
                      {metrics.coverage.measured_share != null && <>
                        {' '}({Math.round(metrics.coverage.measured_share * 100)}% of earnings)
                      </>}; the rest are marked ° and share what is left over
                      (impressions × the site&apos;s leftover RPM).</>
                  : <> AdSense reported no pages individually, so every RPM here is estimated.</>
              )}
            </div>
          </div>
        </div>
      )}

      <Alert type="error" onDismiss={() => setError('')}>{error}</Alert>

      {loading && <Spinner size={40} label="Loading pages…" />}

      {/* Bulk toolbar: only when something is ticked, so the page reads as a
          list until the user starts acting on it. */}
      {!loading && selected.size > 0 && (
        <div className="toolbar" role="region" aria-label="Bulk actions"
             style={{ padding: '8px 12px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
          <strong>{selected.size} selected</strong>
          <button type="button" className="btn btn-primary btn-sm" disabled={bulkBusy} onClick={() => setPendingAction('published')}>Publish</button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={bulkBusy} onClick={() => setPendingAction('draft')}>Set draft</button>
          <button type="button" className="btn btn-danger btn-sm" disabled={bulkBusy} onClick={() => setPendingAction('archived')}>Archive</button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={bulkBusy} onClick={clearSelection} style={{ marginLeft: 'auto' }}>Clear selection</button>
        </div>
      )}

      {!loading && pages.length > 0 && sortedPages.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '36px' }}>
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleVisible}
                      aria-label={allVisibleSelected ? 'Deselect all visible pages' : 'Select all visible pages'}
                      title="Select all rows on this page"
                    />
                  </th>
                  <th className="sortable" onClick={() => handleSort('title')}>
                    Page{sortIndicator('title')}
                  </th>
                  <th className="sortable" style={{ minWidth: '90px' }}
                      title="Draft, Scheduled (published with a future date, or a draft the daily publish queue has dated), Published or Archived"
                      onClick={() => handleSort('state')}>
                    State{sortIndicator('state')}
                  </th>
                  <th className="sortable num" title="Page views over the last 30 complete MST days"
                      onClick={() => handleSort('hits')}>
                    Hits (30d){sortIndicator('hits')}
                  </th>
                  {humanKnown && (
                    <th className="sortable num"
                        title={(metrics.human_rule && metrics.human_rule.text) || 'Page views that are probably human'}
                        onClick={() => handleSort('human')}>
                      Human{sortIndicator('human')}
                    </th>
                  )}
                  <th className="sortable num"
                      title="Share of this page's views where the reader was active for at least 30 seconds"
                      onClick={() => handleSort('readRate')}>
                    Read rate{sortIndicator('readRate')}
                  </th>
                  <th className="sortable num"
                      title={'Median active time of the readers who stayed 30s+: half of them stayed at least '
                           + 'this long. Muted when fewer than ' + READ_MIN_SAMPLE + ' readers.'}
                      onClick={() => handleSort('readTime')}>
                    Read time{sortIndicator('readTime')}
                  </th>
                  <th className="sortable num" title="Ad impressions over the same 30 days"
                      onClick={() => handleSort('impressions')}>
                    Impr (30d){sortIndicator('impressions')}
                  </th>
                  <th className="sortable num" title="Ads served per page view"
                      onClick={() => handleSort('adsPerHit')}>
                    Ads/Hit{sortIndicator('adsPerHit')}
                  </th>
                  <th className="sortable num"
                      title={'eCPM (AdSense "Impression RPM"): earnings per 1,000 ad impressions. '
                           + 'What an ad slot on this page is worth, independent of how many it shows.'}
                      onClick={() => handleSort('ecpm')}>
                    eCPM{sortIndicator('ecpm')}
                  </th>
                  <th className="sortable num"
                      title={'Page RPM (AdSense "Page views RPM"): earnings per 1,000 page views. '
                           + 'Page RPM = eCPM x Ads/Hit. Values marked ° are estimated: AdSense '
                           + 'only reports pages above an impression threshold, so the remainder share '
                           + 'whatever earnings are left over, by impression share.'}
                      onClick={() => handleSort('pageRpm')}>
                    Page RPM{sortIndicator('pageRpm')}
                  </th>
                  <th className="sortable num"
                      title={'Total AdSense earnings for this page over the last 30 days. Measured where AdSense '
                           + 'reported the page by URL; values marked ° are estimated (page impressions × the '
                           + 'site\'s leftover RPM). Per-page values add up to the site\'s AdSense total. '
                           + 'Amazon earnings are per site only and are not included.'}
                      onClick={() => handleSort('earnings')}>
                    Earnings (30d){sortIndicator('earnings')}
                  </th>
                  {hasSearch && (
                    <th className="sortable num" style={{ minWidth: '120px' }}
                        title={`Google search, last ${SEARCH_DAYS} days (Search Console): the page's average position and clicks. `
                             + 'Below, what its queries would add a month at the top 3 (the Rankings model), '
                             + 'credited to the page Google shows most for each query. Sorts by that upside.'}
                        onClick={() => handleSort('google')}>
                      Google{sortIndicator('google')}
                    </th>
                  )}
                  {showOffers && (
                    <th className="sortable num"
                        title="Amazon offers on this page seen at least half in view (once per offer per page load), last 30 days. Our Snowplow count, not Amazon's."
                        onClick={() => handleSort('amzImpr')}>
                      Amz impr{sortIndicator('amzImpr')}
                    </th>
                  )}
                  {showOffers && (
                    <th className="sortable num"
                        title="Clicks on this page's Amazon offer links, last 30 days, with click-through rate (clicks / offer impressions). Our Snowplow count, not Amazon's."
                        onClick={() => handleSort('amzClicks')}>
                      Amz clicks{sortIndicator('amzClicks')}
                    </th>
                  )}
                  <th style={{ minWidth: '140px' }}>
                    Trend (30d)
                    <div style={{ fontWeight: 400, fontSize: '10px', marginTop: '2px' }}>
                      <span style={{ color: SERIES_COLORS.hits }}>&#9473; hits</span>
                      {' '}
                      <span style={{ color: SERIES_COLORS.impressions }}>&#9473; impr</span>
                    </div>
                  </th>
                  <th className="sortable" onClick={() => handleSort('updated')}>
                    Updated{sortIndicator('updated')}
                  </th>
                  <th className="actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((page, index) => {
                  const id = rowId(page);
                  const m = metricsForPage(metricsIndex, page);
                  const trendSeries = [
                    { key: 'hits', color: SERIES_COLORS.hits, data: dailyToSeries(m.hits_daily, dayKeys) },
                    { key: 'impressions', color: SERIES_COLORS.impressions, data: dailyToSeries(m.impressions_daily, dayKeys) },
                  ];
                  const estimated = isEstimated(m);
                  const o = offersForPage(offersIndex, page);
                  const g = searchForPage(searchIndex, page);
                  const gMove = positionMove(g.position, g.prevPosition);
                  const state = derivePageState(page);
                  const attention = pageAttentionBadges(page);
                  const thinVerdict = pageVerdict(thinData && thinData.pages, page);
                  const isSelected = selected.has(id);
                  const hasRevision = revisionIds.has(id);
                  const external = notDamPage(page);
                  return (
                    <tr
                      key={id || index}
                      className={external ? undefined : 'clickable'}
                      aria-selected={isSelected}
                      style={isSelected ? { background: '#eff6ff' } : undefined}
                      onClick={() => handleEditPage(page)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={external}
                          onChange={() => toggleOne(id)}
                          aria-label={`Select ${page.title || page.slug || 'page'}`}
                        />
                      </td>
                      <td style={{ maxWidth: '320px' }}>
                        <strong>{page.title || (page.source === 'stub' ? 'Site template page' : 'Untitled')}</strong>
                        {page.slug && (
                          <div className="muted mono" style={{ fontSize: '12px', marginTop: '3px', wordBreak: 'break-all' }}>
                            /{page.slug}
                          </div>
                        )}
                        {page.description && (
                          <div className="muted" style={{ fontSize: '12px', marginTop: '3px' }}>{page.description}</div>
                        )}
                        {(external || attention.length > 0 || thinVerdict?.thin || hasRevision) && (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '5px' }}>
                            {page.source === 'template' && (
                              <Badge state="info" title="Rendered by the site's template; the title and description are a snapshot of the live page">
                                Template{page.searchable ? ' · in search' : ''}
                              </Badge>
                            )}
                            {page.source === 'stub' && (
                              <Badge state="warn" title="No DAM page: the site serves this URL from a template, and this record only holds its ad stats">
                                No DAM page
                              </Badge>
                            )}
                            {hasRevision && (
                              <Badge state="info" title="Edits saved as a revision: visible in preview only until published">Pending revision</Badge>
                            )}
                            {attention.map((b) => <Badge key={b.key} state={b.state}>{b.label}</Badge>)}
                            <ThinBadge verdict={thinVerdict} />
                          </div>
                        )}
                      </td>
                      <td>
                        <Badge
                          state={state}
                          title={state === 'scheduled' && goLiveAt(page)
                            ? `Goes live ${new Date(goLiveAt(page)).toLocaleString()}${page.status === 'draft' ? ' (publish queue)' : ''}`
                            : STATE_LABELS[state]}
                        />
                      </td>
                      <td className="num">{m.hits > 0 ? formatCount(m.hits) : <Dash />}</td>
                      {humanKnown && (
                        <td className="num"
                            title={m.hits > 0 && m.human_hits != null
                              ? `${formatCount(m.human_hits)} of ${formatCount(m.hits)} views probably human; ${formatCount(m.hits - m.human_hits)} unverified`
                              : undefined}>
                          {m.hits > 0 && m.human_hits != null ? (
                            <>
                              {formatCount(m.human_hits)}
                              <div className="muted" style={{ fontSize: '11px' }}>{Math.round((m.human_hits / m.hits) * 100)}%</div>
                            </>
                          ) : <Dash />}
                        </td>
                      )}
                      <td className="num">{m.read_rate == null ? <Dash /> : formatCtr(m.read_rate)}</td>
                      <td className="num">
                        {m.read_median_s == null
                          ? <Dash />
                          : (
                            <span className={m.readers < READ_MIN_SAMPLE ? 'muted' : undefined}
                                  title={`${formatCount(m.readers)} of ${formatCount(m.views_tracked)} views stayed 30s+`}>
                              {formatDuration(m.read_median_s)}
                            </span>
                          )}
                      </td>
                      <td className="num">{m.impressions > 0 ? formatCount(m.impressions) : <Dash />}</td>
                      <td className="num">{m.ads_per_hit == null ? <Dash /> : formatRatio(m.ads_per_hit)}</td>
                      <td className="num">
                        {m.ecpm == null
                          ? <Dash />
                          : <span className={estimated ? 'muted' : undefined}>{formatMoney(m.ecpm)}</span>}
                      </td>
                      <td className="num">
                        {m.page_rpm == null
                          ? <Dash />
                          : (
                            <span className={estimated ? 'muted' : undefined}
                                  style={estimated ? undefined : { fontWeight: 600 }}
                                  title={estimated
                                    ? 'Estimated: AdSense did not report this page individually, so it '
                                      + 'takes a share of the leftover earnings by impressions.'
                                    : 'Measured: AdSense reported earnings for this page.'}>
                              {formatMoney(m.page_rpm)}{estimated ? '°' : ''}
                            </span>
                          )}
                      </td>
                      <td className="num">
                        {m.earnings == null
                          ? <Dash />
                          : (
                            <span className={estimated ? 'muted' : undefined}
                                  style={estimated ? undefined : { fontWeight: 600 }}
                                  title={estimated
                                    ? 'Estimated: impressions × the site\'s leftover RPM (AdSense did not report this page by URL)'
                                    : 'Measured: AdSense reported earnings for this page'}>
                              {formatMoney(m.earnings)}{estimated ? '°' : ''}
                            </span>
                          )}
                      </td>
                      {hasSearch && (
                        <td className="num">
                          {g.impressions > 0 || g.queries.length > 0 ? (
                            <span title={[
                              g.topQuery ? `Top query: “${g.topQuery}”.` : null,
                              `${formatCount(g.impressions)} impressions, ${formatCount(g.clicks)} clicks.`,
                              gMove ? `${gMove.label} places on the ${SEARCH_DAYS} days before.` : null,
                              g.best ? `Best to push: “${g.best.query}” at ${formatPosition(g.best.position)}, `
                                + `~${formatCount(Math.round(g.best.gain_top3_clicks))} more clicks a month at the top 3.` : null,
                            ].filter(Boolean).join(' ')}>
                              <span style={{ fontWeight: 600 }}>{formatPosition(g.position)}</span>
                              {gMove && (
                                <span className="small" style={{ color: gMove.up ? 'var(--color-success)' : 'var(--color-danger)', marginLeft: 4 }}>
                                  {gMove.up ? '▲' : '▼'}
                                </span>
                              )}
                              <span className="muted"> · {formatCount(g.clicks)}</span>
                              {g.upsideUsd >= 0.01 && (
                                <div style={{ fontSize: '11px', color: 'var(--color-success)', whiteSpace: 'nowrap' }}>
                                  +{fmtUsd(g.upsideUsd)}/mo at top 3
                                </div>
                              )}
                            </span>
                          ) : <Dash />}
                        </td>
                      )}
                      {showOffers && (
                        <td className="num">{o.impressions > 0 ? formatCount(o.impressions) : <Dash />}</td>
                      )}
                      {showOffers && (
                        <td className="num">
                          {o.impressions > 0 || o.clicks > 0 ? (
                            <>
                              {formatCount(o.clicks)}
                              <div className="muted" style={{ fontSize: '11px' }}>{formatCtr(o.ctr)}</div>
                            </>
                          ) : <Dash />}
                        </td>
                      )}
                      <td><Sparkline series={trendSeries} /></td>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(page.updated_at)}</td>
                      <td className="actions" onClick={(e) => e.stopPropagation()}>
                        {!external && (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleEditPage(page)} title="Edit page">
                            Edit
                          </button>
                        )}
                        <a
                          className="btn btn-ghost btn-sm"
                          href={`https://${hostname}/${page.slug || ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View live page"
                        >
                          Live ↗
                        </a>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleSitePreview(page)}
                          title={`Open on ${hostname} in preview: drafts and pending revisions show for you only`}
                        >
                          Preview ↗
                        </button>
                        {canPreview && (
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPreviewSlug(page.slug)} title="Preview in the console, on the development renderer">
                            Dev preview
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paging. Chip counts and the "of N" here both describe the whole
              filtered set, so the page controls never disagree with the chips. */}
          <div className="toolbar" style={{ marginTop: '12px', justifyContent: 'space-between' }}>
            <span className="muted" style={{ fontSize: '13px' }}>
              Showing {paged.start.toLocaleString()}–{paged.end.toLocaleString()} of {paged.total.toLocaleString()}
              {stateFilter !== 'all' || query || thinOnly ? ' matching' : ''}{thinOnly ? ' thin' : ''} pages
            </span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              {!showAll && paged.totalPages > 1 && (
                <>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={paged.page <= 1} onClick={() => setPageNum(paged.page - 1)}>
                    ← Prev
                  </button>
                  <span style={{ fontSize: '13px' }}>Page {paged.page} of {paged.totalPages}</span>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={paged.page >= paged.totalPages} onClick={() => setPageNum(paged.page + 1)}>
                    Next →
                  </button>
                </>
              )}
              {paged.total > PAGE_SIZE && (
                <button
                  type="button"
                  className={`chip${showAll ? ' active' : ''}`}
                  aria-pressed={showAll}
                  onClick={() => setShowAll(v => !v)}
                  title={showAll ? `Back to ${PAGE_SIZE} per page` : 'Show every matching page on one screen'}
                >
                  {showAll ? `${PAGE_SIZE} per page` : 'Show all'}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {!loading && pages.length === 0 && !error && (
        <EmptyState
          title="No pages yet"
          message="This site has no pages. Create the first one to get started."
          action={(
            <button type="button" className="btn btn-primary" onClick={() => navigate(`/domain/${domainId}/pages/new/edit`)}>
              + New page
            </button>
          )}
        />
      )}

      {!loading && pages.length > 0 && sortedPages.length === 0 && (
        <EmptyState
          title={`No ${thinOnly ? 'thin ' : ''}${stateFilter === 'all' ? '' : STATE_LABELS[stateFilter].toLowerCase() + ' '}pages${query ? ` match "${query}"` : ''}`}
          message="Try another state or clear the search."
          action={(
            <button type="button" className="btn btn-secondary" onClick={() => { setStateFilter('all'); setQuery(''); setThinOnly(false); }}>
              Clear filters
            </button>
          )}
        />
      )}

      <TemplateRoutes domainId={domainId} liveUrl={`https://${hostname}`} onChange={fetchPages} />

      {previewSlug !== null && (
        <PagePreviewModal
          domainId={domainId}
          slug={previewSlug}
          onClose={() => setPreviewSlug(null)}
        />
      )}

      <ConfirmDialog
        open={!!pendingAction}
        title={pendingMeta ? `${pendingMeta.label} ${selected.size} page${selected.size === 1 ? '' : 's'}?` : ''}
        message={pendingMeta ? pendingMeta.note : ''}
        confirmLabel={pendingMeta ? pendingMeta.label : 'Confirm'}
        danger={!!pendingMeta && pendingMeta.danger}
        onConfirm={runBulk}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
};

export default DomainPages;
